import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { restorePurchases } from '~/session/commerce.js';
import { useAuthStore } from '~/store/authStore.js';
import { Button, ButtonLink } from '~/ui/Button.js';
import { Section } from '~/ui/Section.js';
import {
  FOCUS,
  HEADLINE,
  type RestoreStep,
  SUBSCRIPTION_PATH,
  type SubscriptionState,
  confirmedMember,
  onward,
  signInFirst,
  standingOf,
  subscriptionPath,
  useStoreCall,
  useSubscriptionPreview,
} from './subscriptionPages.js';

/**
 * Restore purchases, `commerce.restore`, opened from the Purchases band on the
 * Subscription page or the offer.
 *
 * What it owes the player (`docs/ux/commerce/restore.md`): say what restore
 * checks before it starts, a busy state in text that stops a second press, the
 * result in text, a support path that survives failure, and a way back. So the
 * page is one band. As opened, it explains the check and has the one lemon.
 * Checking keeps the explanation, says so under it, and disables the lemon.
 * Each result is a headline in the display face, one line, and the way out:
 * Done back to where restore was opened, or on failure Try again and Get help.
 *
 * Nothing spins: the busy state and every result are sentences, in a status
 * region. A store's "found" is checked against `/me` before it is believed,
 * and reads as pending until `/me` shows the plan.
 *
 * Restore checks the account, so a guest is asked to sign in instead.
 */
export function RestorePage({ state }: { readonly state: SubscriptionState }): React.JSX.Element {
  const { t } = useTranslation();
  const preview = useSubscriptionPreview();
  const guest = useAuthStore((auth) => standingOf(auth.user) === 'guest');
  const [step, setStep] = useState<RestoreStep>(preview.restore ?? 'ready');
  const call = useStoreCall();
  const actions = useRef<HTMLDivElement>(null);
  const answered = useRef(false);
  const asking = step === 'ready' || step === 'checking';

  /* An answer replaces the button that was pressed, so focus moves to the
     first way on: Try again after a failure, Done after the rest. */
  useEffect(() => {
    if (!answered.current || asking) return;
    actions.current?.querySelector<HTMLElement>('a, button')?.focus();
  }, [step, asking]);

  // A press while the check is out does nothing, and the button keeps focus.
  const restore = (): void => {
    if (call.busy || step === 'checking') return;
    setStep('checking');
    call.run(restorePurchases, 'failed', async (answer, ours) => {
      const found = answer === 'found' ? await confirmedMember() : false;
      if (!ours()) return;
      answered.current = true;
      // Found is believed only once `/me` shows it; until then it is pending.
      setStep(answer === 'found' ? (found ? 'found' : 'pending') : answer);
    });
  };

  const origin =
    state.restoreFrom === 'offer'
      ? {
          to: subscriptionPath('offer'),
          state: { ...onward(state), returnTo: FOCUS.restore },
        }
      : { to: SUBSCRIPTION_PATH, state: { returnTo: FOCUS.restore } };
  const help = <ButtonLink to="/settings/support">{t('subscription.restore.help')}</ButtonLink>;

  return (
    <Section title={t('subscription.restore.title')}>
      {asking && (
        <>
          <p className="text-muted">{t('subscription.restore.intro')}</p>
          <p className="mt-2 text-muted">{t('subscription.restore.follows')}</p>
          {guest && <p className="mt-2 text-muted">{t('subscription.restore.guestLine')}</p>}
        </>
      )}
      <div role="status">
        {step === 'checking' && (
          <p className="mt-4 text-ink">{t('subscription.restore.checking')}</p>
        )}
        {!asking && (
          <>
            <p className={HEADLINE}>{t(`subscription.restore.${step}`)}</p>
            <p className="mt-2 text-muted">{t(`subscription.restore.${step}Line`)}</p>
          </>
        )}
      </div>
      <div ref={actions} className="mt-4 flex flex-col gap-3">
        {asking && guest && (
          // Signed in, the guest comes back here, with where restore was opened.
          <ButtonLink
            variant="primary"
            {...signInFirst(subscriptionPath('restore'), {
              ...onward(state),
              restoreFrom: state.restoreFrom,
            })}
          >
            {t('auth.signIn')}
          </ButtonLink>
        )}
        {/* One button from Restore through checking to Try again, so a press
            never takes focus away from it: it refuses presses while the check
            is out, and only its words change. */}
        {!guest && (asking || step === 'failed') && (
          <Button
            variant="primary"
            aria-disabled={step === 'checking' ? true : undefined}
            onClick={restore}
          >
            {step === 'failed' ? t('subscription.restore.retry') : t('subscription.restore.go')}
          </Button>
        )}
        {step === 'failed' && help}
        {!asking && step !== 'failed' && (
          <>
            <ButtonLink variant="primary" to={origin.to} state={origin.state}>
              {t('subscription.restore.done')}
            </ButtonLink>
            {/* A player sure they subscribed needs a way on besides Done. */}
            {step === 'none' && help}
          </>
        )}
      </div>
    </Section>
  );
}
